import React, { useEffect, useState } from "react";
import { Grid, LinearProgress, Paper, Skeleton, Box, TextField, Button, Divider, Typography, useTheme, useMediaQuery } from "@mui/material";
import { useOfficialDailyController } from "./context/official-daily-controller";
import { useParams } from "react-router-dom";
import { useNavigate } from "../../hooks/useNavigate";
import { useOfficialDailyState } from "./context/official-daily-context";
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import DescriptionIcon from '@mui/icons-material/Description';
import dayjs, { Dayjs } from 'dayjs';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { DataGrid, GridColDef, GridPaginationModel } from "@mui/x-data-grid";
import { IListOfficialDailyRequest } from "../../model/base/request/list-official-daily-request";
import { IOfficialDailyResponse } from "../../model/base/response/official-daily-response";
import PageHeader from "../components/shared/page-header";
import ClearIcon from '@mui/icons-material/Clear';

export function OfficialDailyComponent() {
    const [initialLoading, setInitialLoading] = useState(true)
    const [loadMoreData, setLoadMoreData] = useState(false);
    const { loadData } = useOfficialDailyController();
    const { year } = useParams();
    const navigate = useNavigate();
    const { officialDaily } = useOfficialDailyState();
    const [paramsRequet, setParamsRequest] = useState<IListOfficialDailyRequest>({ year: year, page: 0, pageSize: 10 } as IListOfficialDailyRequest);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const formatDate = (dateString?: string) => {
        if (!dateString) return '-';
        return dayjs(dateString).format('DD/MM/YYYY');
    };

    useEffect(() => {
        const searchData = async () => {
            setLoadMoreData(true);
            await loadData(paramsRequet);
            setLoadMoreData(false);
            setInitialLoading(false);

            console.log(officialDaily);
        };
        searchData();
    }, [paramsRequet]);

    const handleClearFilters = () => {
        setParamsRequest({ year: year, page: 0, pageSize: 10 } as IListOfficialDailyRequest);
    };

    const columns: GridColDef<IOfficialDailyResponse>[] = [
        {
            field: 'editionNumber',
            headerName: 'N Edição',
            flex: 1,
            renderCell: (params) => {
                return [
                    <React.Fragment>
                        {params.row.editionNumber}/{params.row.yearPublication}
                    </React.Fragment>
                ];
            }
        },
        {
            field: 'description',
            headerName: 'Descrição',
            flex: 4,
            renderCell: (params) => {
                return (
                    <Box
                        sx={{
                            whiteSpace: 'pre-line',
                            wordWrap: 'break-word',
                            overflow: 'visible',
                            textOverflow: 'clip',
                            display: 'block',
                            width: '100%'
                        }}
                    >
                        {params.row.description}
                    </Box>
                );
            }
        }, {
            field: 'publicationDate',
            headerName: 'Data de Publicação',
            flex: 1,
            valueFormatter: params => dayjs(params?.value).format('DD/MM/YYYY'),

        },
        {
            field: 'detalhes',
            headerName: 'Ações',
            width: 200,
            headerAlign: 'center',
            align: 'center',
            sortable: false,
            renderCell: (params) => {
                return (
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 0.5,
                            alignItems: 'center',
                            width: '100%'
                        }}
                    >
                        <Button
                            variant="text"
                            size="small"
                            endIcon={<DescriptionIcon />}
                            href={params.row.fileViewUrl}
                            target="_blank"
                            rel="noopener"
                            component="a"
                            sx={{
                                textTransform: 'none',
                                fontWeight: 500,
                                minWidth: '130px',
                                padding: '4px 12px'
                            }}
                        >
                            Ver Arquivo
                        </Button>
                        <Button
                            variant="text"
                            size="small"
                            endIcon={<ArrowForwardIcon />}
                            onClick={() => navigate(`/diario-oficial/detalhes/${params.row.id}`)}
                            sx={{
                                textTransform: 'none',
                                fontWeight: 500,
                                minWidth: '130px',
                                padding: '4px 12px'
                            }}
                        >
                            Ver Detalhes
                        </Button>
                    </Box>
                );
            }
        }
    ];

    return (
        <Box>
            <PageHeader
                title={year ? `Edições do Diário Oficial ${year}` : 'Edições do Diário Oficial'}
                subtitle="Acesse as publicações oficiais do município, incluindo leis, decretos, portarias e demais atos administrativos. Todas as edições estão disponíveis para consulta e download."
                breadcrumbs={[{ label: 'Edições do Diário Oficial' }]}
            />

            {initialLoading ? (
                <Skeleton variant="rounded" width="100%" height={500} sx={{ borderRadius: 2 }} />
            ) : (
                <React.Fragment>
                    <Paper sx={{ p: 3, mb: 3 }} elevation={2}>
                        <Grid container spacing={2} alignItems="center">
                            <Grid item xs={12} sm={6} md={5}>
                                <TextField
                                    fullWidth
                                    label="Buscar por descrição"
                                    placeholder="Digite para buscar na descrição"
                                    value={paramsRequet.term || ''}
                                    onChange={(e) => {
                                        setParamsRequest({ ...paramsRequet, page: 0, term: e.target.value });
                                    }}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6} md={2.5}>
                                <LocalizationProvider dateAdapter={AdapterDayjs}>
                                    <DatePicker
                                        label="Data de"
                                        format="DD/MM/YYYY"
                                        value={paramsRequet.startDate ? dayjs(paramsRequet.startDate) : null}
                                        onChange={(e: Dayjs | null) => setParamsRequest({ ...paramsRequet, page: 0, startDate: e?.format('YYYY-MM-DD') })}
                                        slotProps={{ textField: { fullWidth: true } }}
                                    />
                                </LocalizationProvider>
                            </Grid>
                            <Grid item xs={12} sm={6} md={2.5}>
                                <LocalizationProvider dateAdapter={AdapterDayjs}>
                                    <DatePicker
                                        label="Data até"
                                        format="DD/MM/YYYY"
                                        value={paramsRequet.endDate ? dayjs(paramsRequet.endDate) : null}
                                        onChange={(e: Dayjs | null) => setParamsRequest({ ...paramsRequet, page: 0, endDate: e?.format('YYYY-MM-DD') })}
                                        slotProps={{ textField: { fullWidth: true } }}
                                    />
                                </LocalizationProvider>
                            </Grid>
                            <Grid item xs={12} md={2}>
                                <Button
                                    variant="outlined"
                                    color="primary"
                                    startIcon={<ClearIcon />}
                                    onClick={handleClearFilters}
                                    fullWidth
                                    sx={{ height: '56px' }}
                                >
                                    Limpar Filtros
                                </Button>
                            </Grid>
                        </Grid>
                    </Paper>

                    <Paper sx={{ p: 3, minHeight: 500 }} elevation={2}>
                        {isMobile ? (
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                {(officialDaily.items || []).map((daily) => (
                                    <Paper key={daily.id} elevation={2} sx={{ borderRadius: 2, overflow: 'hidden' }}>
                                        <Box
                                            sx={{
                                                backgroundColor: 'primary.main',
                                                px: 2,
                                                py: 1,
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 1,
                                            }}
                                        >
                                            <DescriptionIcon sx={{ fontSize: 24, color: 'white' }} />
                                            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'white', flex: 1 }}>
                                                Edição {daily.editionNumber}/{daily.yearPublication}
                                            </Typography>
                                        </Box>
                                        <Box sx={{ px: 2, py: 1.5 }}>
                                            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase' }}>
                                                Data de Publicação
                                            </Typography>
                                            <Typography variant="body2" sx={{ mb: 1 }}>
                                                {formatDate(daily.publicationDate)}
                                            </Typography>
                                            {daily.description && (
                                                <>
                                                    <Divider sx={{ my: 1 }} />
                                                    <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-line', lineHeight: 1.5 }}>
                                                        {daily.description}
                                                    </Typography>
                                                </>
                                            )}
                                        </Box>
                                        <Divider />
                                        <Box sx={{ px: 2, py: 1, display: 'flex', flexWrap: 'wrap', gap: 0.5, justifyContent: 'flex-end' }}>
                                            <Button
                                                variant="text"
                                                size="small"
                                                endIcon={<DescriptionIcon />}
                                                href={daily.fileViewUrl}
                                                target="_blank"
                                                rel="noopener"
                                                component="a"
                                                sx={{ textTransform: 'none', fontWeight: 500 }}
                                            >
                                                Ver Arquivo
                                            </Button>
                                            <Button
                                                variant="text"
                                                size="small"
                                                endIcon={<ArrowForwardIcon />}
                                                onClick={() => navigate(`/diario-oficial/detalhes/${daily.id}`)}
                                                sx={{ textTransform: 'none', fontWeight: 500 }}
                                            >
                                                Ver Detalhes
                                            </Button>
                                        </Box>
                                    </Paper>
                                ))}
                            </Box>
                        ) : (
                        <DataGrid
                            key={JSON.stringify(paramsRequet)}
                            rows={officialDaily.items || []}
                            rowCount={officialDaily.totalRecords || 0}
                            columns={columns}
                            getRowId={(row) => row.id!}
                            hideFooterSelectedRowCount
                            paginationModel={{ page: paramsRequet.page, pageSize: paramsRequet.pageSize } as GridPaginationModel}
                            onPaginationModelChange={(e) => { setParamsRequest({ ...paramsRequet, page: e.page, pageSize: e.pageSize }) }}
                            pageSizeOptions={[5]}
                            paginationMode="server"
                            getRowHeight={() => 'auto'}
                            slots={{
                                loadingOverlay: LinearProgress,
                            }}
                            loading={loadMoreData}
                            localeText={{
                                MuiTablePagination: {
                                    labelDisplayedRows: ({ from, to, count }) =>
                                        `${from}–${to} de ${count !== -1 ? count : `mais de ${to}`}`,
                                    labelRowsPerPage: 'Linhas por página:',
                                },
                                noRowsLabel: 'Nenhum registro encontrado',
                                noResultsOverlayLabel: 'Nenhum resultado encontrado',
                            }}
                            sx={{
                                '& .MuiDataGrid-cell': {
                                    display: 'flex',
                                    alignItems: 'center',
                                    lineHeight: 'normal !important',
                                    maxHeight: 'none !important',
                                    minHeight: '52px !important',
                                    whiteSpace: 'normal',
                                    py: 1.5,
                                },
                                '& .MuiDataGrid-row': {
                                    maxHeight: 'none !important',
                                },
                            }}

                        />
                        )}
                    </Paper>
                </React.Fragment>
            )}
        </Box>
    );
}
